import { Select } from '@99designs/common/components/Select';
import { Industry } from '__generated__/Industry';
import { ProjectAgencyType, ProjectReferralType } from '__generated__/types';
import { Field as DefaultField } from 'common/components/Form';
import RadioList from 'common/components/RadioList';
import Text from 'common/components/Text';
import { handlePromise } from 'common/utils/Bugsnag';
import IndustryField from 'projects/components/BriefFields/IndustryField';
import OtherField from 'projects/components/BriefFields/OtherField';
import ProfileApprovalField from 'projects/components/BriefFields/ProfileApprovalField';
import UrlsField from 'projects/components/BriefFields/UrlsField';
import ContentSetting from 'projects/components/ContentSetting';
import {
    ObservableField as Field,
    ObservableForm,
    Observe,
} from 'projects/components/ObservableForm';
import { trackViewedBriefEvent } from 'projects/events';
import * as React from 'react';

interface Fields {
    industry: string;
    urls: string[];
    agencyType: ProjectAgencyType;
    referralType: ProjectReferralType;
    referral: string;
    profileApproval: boolean;
    other: string;
}

export interface Props<T> {
    form: ObservableForm<T>;
    industries: Industry[];
    brief: { id: string; designers: { displayName: string }[] };
    viewer: { isRepeatClient: boolean };
}

const referralQuestions = new Map([
    [ProjectReferralType.SEARCH, __('Which search engine?')],
    [ProjectReferralType.FRIEND, __("Your friend's email address?")],
    [ProjectReferralType.PODCAST, __('Which podcast?')],
    [ProjectReferralType.WEBSITE, __('Which website?')],
    [ProjectReferralType.OTHER, __('Please specify...')],
    [ProjectReferralType.INFLUENCER, __('Please specify...')],
]);

class AboutYouPage<T extends Fields> extends React.Component<Props<T>> {
    componentDidMount() {
        trackViewedBriefEvent({
            id: this.props.brief.id,
            page: 'AboutYou',
        });
    }

    render() {
        const { form, viewer, industries } = this.props;

        return (
            <ContentSetting>
                <IndustryField form={form} industries={industries} />
                <UrlsField form={form} />

                {!viewer.isRepeatClient && (
                    <Field
                        form={form}
                        name="agencyType"
                        title={__('Is your company a digital, marketing or design agency?')}
                        tooltip={__(
                            'Agency services empower you to make the most of your 99designs experience and ultimately meet the needs of your clients.'
                        )}
                        render={() => (
                            <RadioList
                                options={[
                                    {
                                        value: ProjectAgencyType.YES_INTERESTED,
                                        label: __("Yes and I'm interested in agency services"),
                                    },
                                    {
                                        value: ProjectAgencyType.YES_NOT_INTERESTED,
                                        label: __("Yes and I'm NOT interested in agency services"),
                                    },
                                    {
                                        value: ProjectAgencyType.NO,
                                        label: __("No, I'm not an agency"),
                                    },
                                ]}
                                {...this.props.form.attach('agencyType')}
                            />
                        )}
                    />
                )}

                {!viewer.isRepeatClient && (
                    <Field
                        form={form}
                        name="referralType"
                        title={__('How did you hear about us?')}
                        render={() => (
                            <>
                                <DefaultField>
                                    <Select
                                        variations={['max']}
                                        name="referralType"
                                        value={form.get('referralType')}
                                        onChange={({ value }: { value: string }) => {
                                            handlePromise(
                                                form.set(
                                                    'referralType',
                                                    value as ProjectReferralType
                                                ),
                                                'ReferralType'
                                            );
                                            handlePromise(form.set('referral', ''), 'Referral');
                                        }}
                                        options={[
                                            {
                                                value: '',
                                                disabled: true,
                                                label: __('Select channel'),
                                            },
                                            {
                                                value: ProjectReferralType.SEARCH,
                                                label: __('Search Engine (e.g. Google, Bing)'),
                                            },
                                            {
                                                value: ProjectReferralType.INFLUENCER,
                                                label: __('Influencer or business coach'),
                                            },
                                            {
                                                value: ProjectReferralType.FRIEND,
                                                label: __('Referred by a friend'),
                                            },
                                            {
                                                value: ProjectReferralType.PODCAST,
                                                label: __('Podcast'),
                                            },
                                            {
                                                value: ProjectReferralType.FACEBOOK,
                                                label: __('Facebook'),
                                            },
                                            {
                                                value: ProjectReferralType.INSTAGRAM,
                                                label: __('Instagram'),
                                            },
                                            {
                                                value: ProjectReferralType.TWITTER,
                                                label: __('Twitter'),
                                            },
                                            {
                                                value: ProjectReferralType.YOUTUBE,
                                                label: __('YouTube'),
                                            },
                                            {
                                                value: ProjectReferralType.PINTEREST,
                                                label: __('Pinterest'),
                                            },
                                            {
                                                value: ProjectReferralType.WEBSITE,
                                                label: __('From another website'),
                                            },
                                            {
                                                value: ProjectReferralType.USED_BEFORE,
                                                label: __("I've used 99designs before"),
                                            },
                                            {
                                                value: ProjectReferralType.OTHER,
                                                label: __('Other'),
                                            },
                                        ]}
                                    />
                                </DefaultField>

                                {referralQuestions.has(form.get('referralType')) && (
                                    <Observe
                                        key={form.get('referralType')}
                                        form={form}
                                        name="referral"
                                        render={() => (
                                            <DefaultField
                                                errorMessage={form.getError('referral')}
                                                title={referralQuestions.get(
                                                    form.get('referralType')
                                                )}
                                            >
                                                <Text
                                                    {...form.attach('referral')}
                                                    fill={true}
                                                    autoFocus={true}
                                                />
                                            </DefaultField>
                                        )}
                                    />
                                )}

                                {form.get('referralType') == ProjectReferralType.YOUTUBE && (
                                    <Observe
                                        key={form.get('referralType')}
                                        form={form}
                                        name="referral"
                                        render={() => (
                                            <DefaultField
                                                errorMessage={form.getError('referral')}
                                                title={__('What type of video was it?')}
                                            >
                                                <Select
                                                    name="referral"
                                                    options={[
                                                        {
                                                            value: '',
                                                            disabled: true,
                                                            label: __('Select'),
                                                        },
                                                        {
                                                            value: 'ADVERTISEMENT',
                                                            label: __('An advertisement'),
                                                        },
                                                        {
                                                            value: '99DESIGNS_VIDEO',
                                                            label: __(
                                                                'Another video uploaded by 99designs'
                                                            ),
                                                        },
                                                        {
                                                            value: 'OTHER_VIDEO',
                                                            label: __(
                                                                'A video uploaded by someone else'
                                                            ),
                                                        },
                                                        {
                                                            value: 'DONT_KNOW',
                                                            label: __("I don't know"),
                                                        },
                                                    ]}
                                                    value={form.get('referral')}
                                                    onChange={({ value }: { value: string }) => {
                                                        handlePromise(
                                                            form.set('referral', value),
                                                            'AboutYouPage'
                                                        );
                                                    }}
                                                    variations={['max']}
                                                />
                                            </DefaultField>
                                        )}
                                    />
                                )}
                            </>
                        )}
                    />
                )}

                <ProfileApprovalField form={form} />
                <OtherField form={form} />
            </ContentSetting>
        );
    }
}

export default AboutYouPage;
